import { createRef, FC, useCallback, useEffect, useState } from "react";

import { Modal } from "@moodys/mdc-frontend.overlays.modal";
import { EventBusTopic } from "@moodys/mdc-frontend.utils.event-bus";
import { getCookieValue } from "@moodys/mdc-toolbox.user.utils";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";

import { MoodysThemeProvider } from "@moodys/federated";
import { config } from "config";
import { useEventBus } from "hooks/useEventBus";
import { setCookie } from "utils/documentCookie";

import { useLayoutContext } from "../../Layout";

const countryIsAustralia = () =>
  getCookieValue(config.MDC_USER_COUNTRY)?.toLowerCase() === "australia";
export const getAustralianModalIsActive = (): boolean => {
  return (
    window.location.pathname === "/" &&
    countryIsAustralia() &&
    getCookieValue(config.AUSTRALIA_CONFIRM_STATE) === undefined
  );
};

export const isAtBottom = (modalContent: HTMLDivElement | null) => {
  if (modalContent) {
    const { scrollTop, scrollHeight, clientHeight } = modalContent;
    return Math.ceil(scrollTop + clientHeight) + 1 >= scrollHeight;
  }
  return false;
};

export const handleAustralianLogIn = () => {
  window.location.href = `${
    window.location.origin
  }/account/sign-in?ReturnUrl=${encodeURIComponent(window.location.pathname)}`;
};

export const onScrollEnd = (
  modalContent: HTMLDivElement | null,
  callback: () => void
) => {
  if (isAtBottom(modalContent)) {
    callback();
  }
};

const blockBodyScroll = (block: boolean) => {
  if (block) {
    document.body.style.height = "100vh";
  } else {
    document.body.style.removeProperty("height");
  }
};

export const AustralianModal: FC = () => {
  const ref = createRef<HTMLDivElement>();
  const [isAuthenticated] = useEventBus(EventBusTopic.IsAuthenticated);
  const [isActive, setIsActive] = useState<boolean>(
    getAustralianModalIsActive()
  );
  const [isConfirmed, setIsConfirmed] = useState<boolean>(
    getCookieValue(config.AUSTRALIA_CONFIRM_STATE) === "true"
  );
  const [readAll, setReadAll] = useState<boolean>(false);
  const { setBlur } = useLayoutContext();

  const handleConfirmWholesaleStatus = useCallback(() => {
    const attrs =
      process.env.ENV !== "localhost" ? { domain: ".moodys.com" } : undefined;
    setCookie({
      cookieName: config.AUSTRALIA_CONFIRM_STATE,
      cookieValue: "true",
      additionalAttributes: attrs,
    });
    setIsActive(false);
    setIsConfirmed(true);
  }, []);

  useEffect(() => {
    setBlur(isActive);
    blockBodyScroll(isActive);
  }, [isActive, setBlur]);

  useEffect(() => {
    if (isAuthenticated && !isConfirmed) {
      handleConfirmWholesaleStatus();
    }
  }, [handleConfirmWholesaleStatus, isAuthenticated, isConfirmed]);

  useEffect(() => {
    const modalContent = ref.current;
    const onScroll = () => onScrollEnd(modalContent, () => setReadAll(true));
    onScroll();
    modalContent?.addEventListener("scroll", onScroll);
    return () => {
      modalContent?.removeEventListener("scroll", onScroll);
    };
  }, [ref]);

  return isActive && !isAuthenticated && !isConfirmed ? (
    <MoodysThemeProvider
      colorPalette="default"
      themeOverrides={{
        components: {
          MuiDialogTitle: {
            styleOverrides: {
              root: {
                "&.MuiTypography-root > .MuiButtonBase-root": {
                  borderRadius: "50%",
                  position: "relative",
                  top: "0px",
                  right: "0px",
                },
              },
            },
          },
        },
      }}
    >
      <Modal
        hideTitleTooltip
        hideCloseButton
        open={isActive && !isAuthenticated}
      >
        <Stack data-testid="australian-modal">
          <Typography fontWeight="fontWeightSemiBold" mb={2}>
            Already a customer?
          </Typography>
          <Button
            onClick={handleAustralianLogIn}
            variant="contained"
            fullWidth
            data-testid="australian-log-in-button"
            disableRipple
          >
            Log In
          </Button>
          <Divider>
            <Typography m={"16px 0 1em"}>OR</Typography>
          </Divider>
          <Typography m={"12px 0px"}>
            Please confirm you are a{" "}
            <Typography
              variant="body2"
              component="span"
              fontWeight="fontWeightSemiBold"
            >
              Wholesale Client
            </Typography>
          </Typography>
          <Box
            data-testid="australian-modal-content-container"
            maxHeight="35vh"
            overflow="auto"
            mb={2}
            ref={ref}
          >
            <Typography data-testid="australian-modal-content">
              Any publication into Australia is pursuant to the Australian
              Financial Services License of Moody&apos;s affiliate, Moody&apos;s
              Investors Service Pty Limited ABN 61 003 399 657 AFSL 336969
              and/or Moody&apos;s Analytics Australia Pty Ltd ABN 94 105 136 972
              AFSL 383569 (as applicable). Moody&apos;s credit ratings and
              publications are intended to be provided only to “wholesale
              clients” within the meaning of section 761G of the Corporations
              Act 2001. By continuing to access, browse or use this Site from
              within Australia, you represent to Moody&apos;s that you are, or
              are accessing the Site as a representative of, a “wholesale
              client” and that neither you nor the entity you represent will
              directly or indirectly disseminate any Materials from this Site to
              “retail clients” within the meaning of section 761G of the
              Corporations Act 2001. Moody&apos;s credit rating is an opinion as
              to the creditworthiness of a debt obligation of the issuer, not on
              the equity securities of the issuer or any form of security that
              is available to retail investors.
            </Typography>
          </Box>
          <Button
            variant="text"
            disabled={!readAll}
            onClick={handleConfirmWholesaleStatus}
            data-testid="australian-confirm-button"
            disableRipple
          >
            Confirm Wholesale Status
          </Button>
        </Stack>
      </Modal>
    </MoodysThemeProvider>
  ) : null;
};
